import { useCallback, useMemo, useState, useEffect } from "react";
import ManagePortsService from "../../services/managePorts.service";
import { areYouSure, throwGenericToasterError, toasterSuccess, wrapInDrawer } from "../../utils/methods";
import { useOpenClose } from "../../utils/hooks/OpenCloseHook";
import { getPopoverUtilityClass } from "@mui/material";
import Input from "../DesignSystem/input";
import { Regex } from "../../utils/appConstants";
import Button from "../DesignSystem/button";

export type ManageLeasedPortsType = {
  loading: boolean;
  moveToLeasedSwitch: (positionInNe: number) => void;
  getAvailablePositionsOnLeasedSwitch: () => void;
  drawerComponent: any;
  openDrawer: any;
};

function useManageLeasedPorts(portId: number | undefined, isMounted: boolean, refreshIndex: () => void): ManageLeasedPortsType {
  const [loading, setLoading] = useState<ManageLeasedPortsType["loading"]>(false);
  const openClose = useOpenClose();
  const [openPositionsList, setOpenPositionsList] = useState<number[]>();
  const [openPositionsRange, setOpenPositionsRange] = useState<string>();
  const [originalPositionInNe, setOriginalPositionInNe] = useState<number>();
  const [positionInNeForm, setPositionInNeForm] = useState<string | number>("");
  const [errorMsg, setErrorMsg] = useState<string>();
  useEffect(() => {
    openClose.isOpen && getAvailablePositionsOnLeasedSwitch();
  }, [portId, openClose.isOpen]);

  const moveToLeasedSwitch = useCallback(
    (positionInNe: number) => {
      if (!portId) return;
      setLoading(true);
      ManagePortsService.moveToLeasedSwitch({
        nrId: portId,
        positionInNe,
      })
        .then((result: any) => {
          if (!isMounted) return;
          setLoading(false);
          refreshIndex();
          toasterSuccess("Port Moved To Switch Successful");
        })
        .catch((e) => {
          if (!isMounted) return;
          setLoading(false);
          throwGenericToasterError(e);
        });
    },
    [portId, isMounted]
  );
  const handleRemoveFromSwitch = useCallback(() => {
    if (!portId) return;
    setLoading(true);
    ManagePortsService.removeFromLeasedSwitch({
      nrId: portId,
    })
      .then((result: any) => {
        if (!isMounted) return;
        setLoading(false);
        refreshIndex();
        toasterSuccess("Port Removed From Switch Successful");
      })
      .catch((e) => {
        if (!isMounted) return;
        setLoading(false);
        throwGenericToasterError(e);
      });
  }, [portId, isMounted]);
  const movePositionOnLeasedSwitch = useCallback(
    (positionInNe: number) => {
      if (!portId) return;
      setLoading(true);
      ManagePortsService.movePositionOnLeasedSwitch({
        nrId: portId,
        positionInNe,
      })
        .then((result: any) => {
          if (!isMounted) return;
          setLoading(false);
          refreshIndex();
          toasterSuccess("Port Position Moved Successful");
        })
        .catch((e) => {
          if (!isMounted) return;
          setLoading(false);
          throwGenericToasterError(e);
        });
    },
    [portId, isMounted]
  );

  const handleSubmit = useCallback(
    (e: any) => {
      if (originalPositionInNe && originalPositionInNe > 0) {
        movePositionOnLeasedSwitch(Number(positionInNeForm));
      } else {
        moveToLeasedSwitch(Number(positionInNeForm));
      }
    },
    [originalPositionInNe, positionInNeForm]
  );

  const handlePositionChange = useCallback(
    (e: any) => {
      if (e.target.value.trim() === "") {
        setPositionInNeForm("");
        setErrorMsg("Position Required");
      } else if (Regex.numbers.test(e.target.value.trim())) {
        if (openPositionsList && !openPositionsList.includes(parseInt(e.target.value.trim()))) {
          setPositionInNeForm(parseInt(e.target.value.trim()));
          setErrorMsg("Position not valid");
        } else {
          setPositionInNeForm(parseInt(e.target.value.trim()));
          setErrorMsg(undefined);
        }
      } else {
        setPositionInNeForm(e.target.value.trim());
        setErrorMsg("Please enter numbers only");
      }
    },
    [openPositionsList]
  );

  const getAvailablePositionsOnLeasedSwitch = useCallback(() => {
    if (!portId) return;
    setLoading(true);

    ManagePortsService.getAvailablePositionsOnLeasedSwitch({
      nrId: portId,
    })
      .then((result: any) => {
        if (!isMounted) return;
        setLoading(false);
        if (result.data) {
          setOpenPositionsList(result.data.array || []);
          setOpenPositionsRange(result.data.range || "None");
          setOriginalPositionInNe(result.data.position_in_ne || "");
        }
      })
      .catch((e) => {
        if (!isMounted) return;
        setLoading(false);
        throwGenericToasterError(e);
      });
  }, [portId, isMounted]);

  const isSubmitDisable = useMemo(() => {
    return !positionInNeForm || Number(originalPositionInNe) === Number(positionInNeForm) || !!errorMsg;
  }, [originalPositionInNe, errorMsg, positionInNeForm]);

  const drawerComponent = useMemo(
    () =>
      wrapInDrawer(
        <div className="putup-forsale-form">
          <div className="add-group-root">
            <div className="title">
              {originalPositionInNe && originalPositionInNe > 0 ? <label>Manage Connection to Switch</label> : <label>Add Connection to Switch</label>}
            </div>
            <br />
            <div>
              <div style={{ display: "flex", flexDirection: "column", fontSize: "20px" }}>
                {originalPositionInNe && originalPositionInNe > 0 && (
                  <label style={{ fontSize: "30px", textAlign: "center", marginBottom: "10px" }}>{`Current Position: ${originalPositionInNe}`}</label>
                )}
                <br />
                <label>New Position:</label>
                <Input
                  variant="input"
                  name="no-of-ports"
                  value={positionInNeForm}
                  min={1}
                  className="no-of-ports-input"
                  errorMessageClass="small-fonts"
                  onChange={handlePositionChange}
                  error={!!errorMsg}
                  errorMessage={errorMsg}
                />
                <span style={{ marginBottom: "20px", fontSize: "14px" }}>{`Available Positions: ${openPositionsRange}`}</span>
              </div>
              <div style={{ marginBlock: "10px" }}>
                <div style={{ paddingBlock: "10px" }}>
                  <Button disabled={isSubmitDisable} variant="primary" onClick={handleSubmit} text="Submit" type="submit" />
                </div>
                <div style={{ paddingBlock: "10px" }}>
                  {originalPositionInNe && originalPositionInNe > 0 && (
                    <Button
                      variant="close"
                      onClick={() => areYouSure(() => handleRemoveFromSwitch(), "Are you sure you want to disconnect this port from the switch")}
                      text="Disconnect from Switch"
                      type="submit"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>,
        openClose.isOpen,
        () => openClose.close(),
        true
      ),
    [openPositionsRange, originalPositionInNe, positionInNeForm, errorMsg, openClose.isOpen, isSubmitDisable]
  );

  const returnProps = useMemo(() => {
    return {
      loading,
      moveToLeasedSwitch,
      drawerComponent,
      getAvailablePositionsOnLeasedSwitch,
      openDrawer: () => openClose.open(),
    };
  }, [loading, drawerComponent]);
  return returnProps;
}

export default useManageLeasedPorts;
